import { gql } from '@apollo/client';
import { crewMemberFragments } from '~/gql/crew-member';

export const timesheetFragments = {
  timesheetCrewMemberItem: gql`
    fragment TimesheetCrewMemberItem on CrewMember {
      id
      firstName
      lastName
      imageUrl
      persona
    }
  `,
};

export const GET_FOREMAN_TIMESHEET = gql`
  query timesheetByForeman($input: TimesheetByForemanInput!) {
    timesheetByForeman(input: $input) {
      id
      error
      timesheetStatus
      crewDetails {
        id
        crewNumber
        crewEmployees {
          id
        }
        foreman {
          ...TimesheetCrewMemberItem
        }
        generalForeman {
          ...TimesheetCrewMemberItem
        }
        superintendent {
          ...TimesheetCrewMemberItem
        }
      }
    }
    ${timesheetFragments.timesheetCrewMemberItem}
  }
`;

export const GET_COST_CODES = gql`
  query costCodes($input: SearchCostCodeInput, $paging: PagingInput) {
    costCodes(input: $input, paging: $paging) {
      cursor
      total
      items {
        costCode
        description
        id
        restricted
        invalid
        unavailable
      }
    }
  }
`;

export const GET_PINNED_COST_CODES_BY_CREW = gql`
  query pinnedCostCodesByCrew($input: PinnedCostCodesByCrewInput!) {
    pinnedCostCodesByCrew(input: $input) {
      timesheetWorkPackages {
        workPackage {
          id
          tag
        }
      }
      timesheetCostCodes {
        id
        claimedOnTimesheetDay
        costCode {
          id
          costCode
          description
        }
      }
    }
  }
`;

export const UNPIN_COST_CODES = gql`
  mutation unpinCostCodes($input: UnpinCostCodesInput!) {
    unpinCostCodes(input: $input) {
      error
      timesheetCostCodeIds
    }
  }
`;

export const PIN_COST_CODES = gql`
  mutation c($input: PinCostCodesInput!) {
    pinCostCodes(input: $input) {
      error
      costCodes {
        timesheetCostCodes {
          id
          costCode {
            id
            costCode
            description
          }
        }
      }
    }
  }
`;

export const GET_TIMESHEET_STATUS_BY_SUPERVISOR = gql`
 query timesheetStatusBySupervisor($input: TimesheetBySupervisorInput!) {
    timesheetStatusBySupervisor(input: $input) {
      crewMember {
        ...TimesheetCrewMemberItem
        discipline
        crew {
          id
          crewNumber
        }
      }
      crewCount
      directReports {
        timesheetStatus
        crewMember {
          id
          firstName
          lastName
          crew {
            id
            crewNumber
          }
        }
        crewCount
      }
    }
    ${timesheetFragments.timesheetCrewMemberItem}
  }
`;

export const GET_TIMESHEET_BY_FOREMAN = gql`
query timesheetByForeman($input: TimesheetByForemanInput!, $forDate: Date!, $crewNumber: String!) {
  timesheetByForeman(input: $input) {
      error
      id
      forDate
      timesheetStatus
      # whether the timesheet is editable at that time of the day!
      # timesheets are not editable once approved or for other reasons
      editable
      crewDetails {
          id
          crewNumber
          foreman {
              id
              firstName
              lastName
              imageUrl
              shiftType
              persona
          }
          generalForeman {
              id
              firstName
              lastName
              imageUrl
              shiftType
              persona
          }
          superintendent {
              id
              firstName
              lastName
              imageUrl
              shiftType
              persona
          }
          crewEmployees {
              ...CrewMemberCompact
          }
          shift {
              id
              roundingIncrement
              dayShiftSchedule(forDate: $forDate, lobCrewNumber: $crewNumber) {
                  id
                  duration
                  hourTypes {
                      id
                      description
                      hourCode
                      maxHours
                      order
                  }
              }
          }
      }
      crewMembers {
          crewMember {
              ...CrewMemberCompact
          }
          shiftPunchIn
          shiftPunchOut
          timeEntries {
              id
              comment {
                  id
                  name
                  value
              }
              hourCode
              hours
              workPackage {
                  id
                  tag
              }
              costCode {
                  id
                  costCode
                  description
                  restricted
                  invalid
              }
          }
      }
  }
  ${crewMemberFragments.crewMemberCompact}
}
`;

// export const GET_TIMESHEET_STATUS_BY_SUPERVISOR = gql`
//   query timesheetStatusBySupervisor($input: TimesheetBySupervisorInput!) {
//     timesheetStatusBySupervisor(input: $input) {
//       crewMember {
//         id
//         firstName
//         lastName
//         imageUrl
//         persona
//         discipline
//         crew {
//           id
//           crewNumber
//         }
//       }
//       directReports {
//         crewMember {
//           id
//           firstName
//           lastName
//           persona
//           discipline
//           crew {
//             id
//             crewNumber
//           }
//         }
//         timesheetStatus
//       }
//     }
//   }
// `;

export const GET_TIMESHEET_REASONS = gql`
  query TimesheetReasonsQuery {
    timesheetReasons {
      id
      name
      value
    }
  }
`;

export const SAVE_TIMESHEET = gql`
  mutation SaveTimesheet($input: SaveTimesheetInput!) {
    saveTimesheet(input: $input) {
      errors {
        error
        crewMemberId
      }
      timesheetDay {
        id
        forDate
        timesheetStatus
        crewMembers {
          crewMember {
            id
            firstName
            lastName
            shiftType
          }
          # following two are needed so cache can update punch in and out from latest data
          shiftPunchIn
          shiftPunchOut
          timeEntries {
            id
            comment {
              id
              name
              value
            }
            hourCode
            hours
            workPackage {
              id
            }
            costCode {
              id
              costCode
              description
              restricted
              invalid
              unavailable
            }
          }
        }
      }
    }
  }
`;

export const SAVE_TIMESHEET_CHARGES = gql`
  mutation SaveTimesheetCharges($input: SaveTimesheetChargesInput!) {
    saveTimesheetCharges(input: $input) {
      errors {
        error
        crewMemberId
      }
      # Load the whole timesheet again as it needs to be cached
      timesheetDay {
        id
        forDate
        timesheetStatus
        # whether the timesheet is editable at that time of the day!
        # timesheets are not editable once approved or for other reasons
        editable
        crewDetails {
          id
          crewNumber
          foreman {
            id
            firstName
            lastName
            imageUrl
            shiftType
          }
          generalForeman {
            id
            firstName
            lastName
          }
        }
        crewMembers {
          crewMember {
            id
            firstName
            lastName
            imageUrl
          }
          shiftPunchIn
          shiftPunchOut
          timeEntries {
            id
            comment {
              id
              name
              value
            }
            hourCode
            hours
            workPackage {
              id
              tag
            }
            costCode {
              id
              costCode
              description
              restricted
              invalid
            }
          }
        }
      }
    }
  }
`;
